import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";
import { NativeSelect as ChakraSelect } from "@chakra-ui/react";
import type { NativeSelectFieldProps } from "@chakra-ui/react";
import { Spinner } from "@ui-kit/feedback/Spinner/Spinner.web";
import ChevronDown from "@ui-kit/icons/ChevronDown/ChevronDown.web";
import { InputButtonProps } from "@ui-kit/form/InputButton/InputButton.web";
import { type BoxProps } from "@ui-kit/layout/Box/Box.web";
import { useSelect } from "downshift";
import { ForwardedRef, ReactElement, forwardRef } from "react";
import Flex from "../../layout/Flex/Flex.web";
import { useSelectHook } from "./hooks";
import { SelectInputItemType, SelectSharedProps } from "./Select.props";

export type SelectProps<T> = SelectSharedProps<T> & {
  componentProps?: {
    root?: Omit<BoxProps, "children">;
    InputButtonProps?: Omit<InputButtonProps, "children">;
  };
  labelFontSize?: string;
  labelFontWeight?: string | number;
  /** CSS background value for the native select element (defaults to unset) */
  background?: string;
};

type SelectContainerProps = {
  children: React.ReactNode;
  leftIcon?: React.ReactNode;
};

const SelectContainer = ({ children, leftIcon }: SelectContainerProps) => {
  return (
    <Flex
      position="relative"
      alignItems="center"
      width="100%"
      _hover={{
        cursor: "pointer",
      }}
    >
      {leftIcon && (
        <Flex position="absolute" left="2xs">
          {leftIcon}
        </Flex>
      )}
      {children}
      <Flex position="absolute" right="2xs" pointerEvents="none" color="fg">
        <ChevronDown name="select-arrow" size="element.lg" />
      </Flex>
    </Flex>
  );
};

// TODO improve typing of accepted item structure, seems to be a union of a generic + an acceptable type that has {disabled: boolean}
const Select = <T extends SelectInputItemType>(
  { leftIcon, background, ...props }: SelectProps<T>,
  ref: ForwardedRef<HTMLSelectElement>
): ReactElement => {
  // props with default values
  const {
    id = "",
    items = [],
    selectedItem = undefined,
    label = "",
    disabled = false,
    placeholder = "",
    ariaLabel = "",
    isInvalid = false,
    isLoading = false,
    dataTestId = undefined,
    onSelectedItemChange,
    disablePlaceholderSelection = false,
    labelFontSize = "16px",
    labelFontWeight = "normal",
    children,
  } = props;

  const { memoizedOnSelectedItemChange, sizeProps, getSelectStyle } =
    useSelectHook(props);

  const { getLabelProps, getToggleButtonProps, getMenuProps } = useSelect({
    items,
    id,
    selectedItem: selectedItem ?? null,
    onSelectedItemChange: memoizedOnSelectedItemChange,
  });

  const { height } = sizeProps;
  const { minWidth } = getSelectStyle();

  const handleChange = (change: React.ChangeEvent<HTMLSelectElement>) => {
    const newSelection = change.target.value;
    const selectedItem =
      items.find(
        // need to use == instead of === because the value is a string, and the id could be a string or a number
        // eslint-disable-next-line eqeqeq
        (item) => item.id == newSelection
      ) ?? ({ id: placeholder, name: placeholder } as T);
    if (selectedItem) {
      onSelectedItemChange({
        selectedItem,
        type: useSelect.stateChangeTypes.ItemClick,
      });
    }
  };

  // Get props but suppress the ref error since we're using a native select
  // This is just to prevent the console error
  getToggleButtonProps({}, { suppressRefError: true });
  getMenuProps({}, { suppressRefError: true });

  return (
    <>
      {label && (
        <FormLabel
          {...getLabelProps()}
          fontSize={labelFontSize}
          fontWeight={labelFontWeight}
        >
          {label}
        </FormLabel>
      )}
      {isLoading ? (
        <Flex
          p="xs"
          height={height}
          justifyContent="center"
          alignItems="center"
        >
          <Spinner size={height} />
        </Flex>
      ) : (
        <SelectContainer leftIcon={leftIcon}>
          <select
            ref={ref}
            id={id}
            value={selectedItem?.id ?? ""}
            onChange={handleChange}
            disabled={disabled}
            aria-label={ariaLabel}
            placeholder={!selectedItem?.name ? placeholder : ""}
            data-testid={dataTestId && `${dataTestId}-input`}
            style={{
              WebkitAppearance: "none",
              MozAppearance: "none",
              appearance: "none",
              color: "fg",
              width: "100%",
              height: height || "40px",
              boxShadow: isInvalid
                ? // "secondary.600" doesnt work, need to use chakra var so themeing the widget works
                  `0 0 0 1px var(--chakra-colors-secondary-600)`
                : "none",
              minWidth,
              boxSizing: "border-box",
              padding: "1px 24px 0px 8px",
              textAlign: "left",
              fontSize: "1rem",
              borderRadius: "4px",
              border: "1px solid",
              borderColor: isInvalid
                ? "var(--chakra-colors-secondary-600)"
                : "var(--chakra-colors-gray-400)",
              outlineColor: "var(--chakra-colors-primary-600)",
              paddingLeft: leftIcon ? "32px" : "8px",
              cursor: "pointer",
              ...(background ? { background } : {}),
            }}
          >
            {/* placeholder first option */}

            {placeholder ? (
              <option
                value={placeholder}
                id="placeholder"
                disabled={disablePlaceholderSelection}
              >
                {placeholder}
              </option>
            ) : null}

            {children !== undefined
              ? children
              : items.map((item) => (
                  <option
                    key={item.id}
                    value={item.id}
                    id={String(item.name)}
                    disabled={item.disabled}
                  >
                    {item.name}
                  </option>
                ))}
          </select>
        </SelectContainer>
      )}
    </>
  );
};

type PrimitiveSelectProps = NativeSelectFieldProps & {
  // Backward compatibility props (v1/v2) - kept for API stability but not used
  // FormControl will handle the validation state
  isInvalid?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
};

const PrimtiveSelect = forwardRef<HTMLSelectElement, PrimitiveSelectProps>(
  function PrimitiveSelect(props, ref) {
    // Extract backward compat props but don't use them - FormControl handles validation
    const {
      isInvalid: _isInvalid,
      isDisabled: _isDisabled,
      isRequired: _isRequired,
      isReadOnly: _isReadOnly,
      ...rest
    } = props;

    return (
      <ChakraSelect.Root>
        <ChakraSelect.Field ref={ref} {...rest} />
      </ChakraSelect.Root>
    );
  }
);

// Use forwardRef with generic type parameter
type SelectComponent = <T extends SelectInputItemType>(
  props: SelectProps<T> & { ref?: ForwardedRef<HTMLSelectElement> }
) => ReactElement;

type SelectWithPrimitive = SelectComponent & {
  Primitive: typeof PrimtiveSelect;
};

const ForwardedSelect = forwardRef(Select) as unknown as SelectWithPrimitive;
ForwardedSelect.Primitive = PrimtiveSelect;

export default ForwardedSelect;
